import React, { useEffect, useState } from 'react';

import { DataGrid, Column, Editing, type DataGridTypes, Lookup } from 'devextreme-react/data-grid';
import { RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';
import notify from 'devextreme/ui/notify';
import { formatMessage } from 'devextreme/localization';

import PathType from '../../consts/pathmodule/PathType';

import PathApi from '../../api/PathApi';
import MetaApiApi from '../../api/MetaApiApi';

import PathListDto from '../../classes/dtos/pathmodule/PathListDto';
import PathFragmentDto, { PathFragmentOperationKey, PathFragmentOperationKeys, PathFragmentOperations } from '../../classes/dtos/metaapimodule/PathFragmentDto';

interface MetaApiPathTemplateProps extends DataGridTypes.MasterDetailTemplateData {
    refresh: () => void;
}

// One column per operation: the localization key of its caption and of its tooltip.
const OPERATION_COLUMNS: { key: PathFragmentOperationKey, caption: string, hint: string }[] = [
    { key: "ReadEnabled", caption: "MetaApisPath_Read", hint: "MetaApisPath_ReadHint" },
    { key: "AddEnabled", caption: "MetaApisPath_Add", hint: "MetaApisPath_AddHint" },
    { key: "AddMultipleEnabled", caption: "MetaApisPath_AddMultiple", hint: "MetaApisPath_AddMultipleHint" },
    { key: "UpdateEnabled", caption: "MetaApisPath_Update", hint: "MetaApisPath_UpdateHint" },
    { key: "UpdateMultipleEnabled", caption: "MetaApisPath_UpdateMultiple", hint: "MetaApisPath_UpdateMultipleHint" },
    { key: "DeleteEnabled", caption: "MetaApisPath_Delete", hint: "MetaApisPath_DeleteHint" },
    { key: "DeleteMultipleEnabled", caption: "MetaApisPath_DeleteMultiple", hint: "MetaApisPath_DeleteMultipleHint" }
];

const NO_OPERATION_ALLOWED: PathFragmentOperations = {
    ReadEnabled: false,
    AddEnabled: false,
    AddMultipleEnabled: false,
    UpdateEnabled: false,
    UpdateMultipleEnabled: false,
    DeleteEnabled: false,
    DeleteMultipleEnabled: false
};

const MetaApiPathTemplate = (props: MetaApiPathTemplateProps) => {
    const { PathFragments } = props.data.data;

    const [paths, setPaths] = useState<PathListDto[] | null>(null);

    useEffect(() => {
        if (paths === null) {
            PathApi.GetPathsAsync(PathType.MetaApi, true).then((e) => {
                let allPaths = e.Result
                    .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                setPaths(allPaths);
            });
        }
    }, []);

    // Operations the path can do, from the capabilities computed when the path was published.
    // A path without capabilities (never published) can only be read.
    function getAllowedOperations(pathId: number | null | undefined): PathFragmentOperations {
        const path = (paths ?? []).find((x) => x.Id === pathId) ?? null;

        if (path === null) {
            return NO_OPERATION_ALLOWED;
        }

        const capabilities = path.Capabilities;

        if (capabilities === null) {
            return { ...NO_OPERATION_ALLOWED, ReadEnabled: true };
        }

        return {
            ReadEnabled: capabilities.CanRead,
            AddEnabled: capabilities.CanAdd,
            AddMultipleEnabled: capabilities.CanAddMultiple,
            UpdateEnabled: capabilities.CanUpdate,
            UpdateMultipleEnabled: capabilities.CanUpdateMultiple,
            DeleteEnabled: capabilities.CanDelete,
            DeleteMultipleEnabled: capabilities.CanDeleteMultiple
        };
    }

    function getOperations(data: any): PathFragmentOperations {
        return {
            ReadEnabled: data.ReadEnabled ?? false,
            AddEnabled: data.AddEnabled ?? false,
            AddMultipleEnabled: data.AddMultipleEnabled ?? false,
            UpdateEnabled: data.UpdateEnabled ?? false,
            UpdateMultipleEnabled: data.UpdateMultipleEnabled ?? false,
            DeleteEnabled: data.DeleteEnabled ?? false,
            DeleteMultipleEnabled: data.DeleteMultipleEnabled ?? false
        };
    }

    const handleOnEditorPreparing = (e: any) => {
        if (e.parentType === 'dataRow' && e.dataField === 'PathId') {
            e.editorOptions.dataSource = paths;
        }

        if (e.parentType === 'dataRow' && PathFragmentOperationKeys.includes(e.dataField)) {
            // Not compatible with the path : the editor is disabled.
            e.editorOptions.disabled = !getAllowedOperations(e.row?.data?.PathId)[e.dataField as PathFragmentOperationKey];
        }
    };

    const handleOnCellPrepared = (e: any) => {
        if (e.rowType === 'data' && PathFragmentOperationKeys.includes(e.column?.dataField)) {
            if (!getAllowedOperations(e.data?.PathId)[e.column.dataField as PathFragmentOperationKey]) {
                e.cellElement.classList.add('metaapi-operation-unavailable');
                e.cellElement.title = formatMessage("MetaApisPath_OperationUnavailable");
            }
        }
    };

    // Changing the path keeps the operations which are still possible and unchecks the others.
    const handlePathIdSetCellValue = (newData: any, value: any, currentRowData: any) => {
        newData.PathId = value;

        const allowed = getAllowedOperations(value);
        let someOperationRemoved = false;

        PathFragmentOperationKeys.forEach((key) => {
            const wasEnabled = currentRowData[key] ?? false;
            const isEnabled = wasEnabled && allowed[key];

            someOperationRemoved = someOperationRemoved || (wasEnabled && !isEnabled);
            newData[key] = isEnabled;
        });

        if (someOperationRemoved) {
            notify(formatMessage("MetaApisPath_OperationsReset"), 'warning', 3000);
        }
    };

    const handleOnInitNewRow = (e: any) => {
        // A new method reads its path, like before the operations exist.
        e.data.ReadEnabled = true;
    };

    const handleOnRowValidating = (e: any) => {
        const merged = { ...(e.oldData ?? {}), ...(e.newData ?? {}) };

        if (!PathFragmentOperationKeys.some((key) => merged[key] === true)) {
            e.isValid = false;
            e.errorText = formatMessage("MetaApisPath_AtLeastOneOperation");
        }
    };

    const handleSourceFieldReferenceIdCalculateDisplayValue = (e: any) => {
        if (paths === null) {
            return;
        }
        let found = paths!.find((x) => x.Id === e.PathId) ?? null;
        return found?.Name ?? "";
    }

    const selectFieldCell = (cellData: DataGridTypes.ColumnCellTemplateData) => {
        if (cellData.value > 0) { return (<div>{cellData.displayValue}</div>); }
        return (<span>{cellData.value || <span style={{ color: "#aaa" }}>Select a field...</span>}</span>);
    };

    function insertRow(e: RowInsertingEvent<PathFragmentDto, any>) {
        MetaApiApi.CreatePathFragmentAsync(props.data.key, e.data.MethodName, e.data.PathId, getOperations(e.data))
            .then((e) =>
            {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                else {
                    props.refresh();
                }
            });
    }

    function updateRow(e: RowUpdatingEvent<PathFragmentDto, any>) {
        let pathFragmentId = e.newData.PathFragmentId ?? e.oldData.PathFragmentId;
        let apiFragmentId = props.data.key;
        let methodName = e.newData.MethodName ?? e.oldData.MethodName;
        let pathId = e.newData.PathId ?? e.oldData.PathId;
        let operations = getOperations({ ...e.oldData, ...e.newData });

        MetaApiApi.UpdatePathFragmentAsync(pathFragmentId, apiFragmentId, methodName, pathId, operations)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                else {
                    props.refresh();
                }
            });
    }

    function removeRow(e: RowRemovingEvent<PathFragmentDto, any>) {

        MetaApiApi.DeletePathFragmentAsync(e.data.PathFragmentId)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                else {
                    props.refresh();
                }
            });
    }

    return (
        <React.Fragment>
            <DataGrid
                dataSource={PathFragments}
                showBorders={true}
                columnAutoWidth={true}
                onEditorPreparing={handleOnEditorPreparing}
                onCellPrepared={handleOnCellPrepared}
                onInitNewRow={handleOnInitNewRow}
                onRowValidating={handleOnRowValidating}
                onRowInserting={insertRow}
                onRowUpdating={updateRow}
                onRowRemoving={removeRow}>
                <Editing
                    mode="row"
                    allowUpdating={true}
                    allowDeleting={true}
                    allowAdding={true}
                    texts={{ addRow: 'Add a new method' }} />
                <Column dataField="MethodName" />
                <Column caption="Path" dataField="PathId" dataType="number" allowEditing={true} setCellValue={handlePathIdSetCellValue} calculateDisplayValue={handleSourceFieldReferenceIdCalculateDisplayValue} cellRender={selectFieldCell} editorOptions={{ placeholder: "Select a path..." }}>
                    <Lookup
                        displayExpr="Name"
                        valueExpr="Id"
                    />
                </Column>
                {OPERATION_COLUMNS.map((operation) => (
                    <Column
                        key={operation.key}
                        dataField={operation.key}
                        caption={formatMessage(operation.caption)}
                        headerCellRender={() => (<span title={formatMessage(operation.hint)}>{formatMessage(operation.caption)}</span>)}
                        dataType="boolean"
                        alignment="center"
                        allowSorting={false}
                        width={90} />
                ))}
            </DataGrid>
        </React.Fragment>
    );
};

export default MetaApiPathTemplate;
